//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';

import { DATE_RANGE_INTERVAL_PARAM } from '@instagram-account/redux/schedule/export/instagramScheduleExportModel';

import {
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  lazyLoadModule,
  NavGroup,
  StateFactory,
} from 'app/initialization/routing/stateHelpers';

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when('/export', '/export/report');
}

export const states = {
  export: StateFactory({
    url: '/export',
    template: componentNameToTemplate('exportPage'),
    data: {
      title: 'Export',
      navGroup: NavGroup.EXPORT,
    },
    resolve: {
      ...lazyLoadModule('curalate.export', () => import('./ExportApp')),
    },
  }),

  'export.report': StateFactory({
    url: '/report?:reportPage',
    views: {
      main: {
        template: componentNameToTemplate('exportReportPage'),
      },
    },
    data: {
      title: 'Download Report',
      navGroup: NavGroup.EXPORT,
    },
  }),

  'export.facebook-summary': StateFactory({
    url: '/facebook-summary-export',
    views: {
      main: {
        template: componentNameToTemplate('facebookSummaryExport'),
      },
    },
    data: {
      title: 'Facebook Summary',
      navGroup: NavGroup.EXPORT,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@export/components/facebook-page-summary-export/FacebookSummaryExport'
          ),
      ),
    },
  }),

  'export.pinterest-summary': StateFactory({
    url: '/pinterest-summary-export',
    views: {
      main: {
        template: componentNameToTemplate('pinterestSummaryExport'),
      },
    },
    data: {
      title: 'Pinterest Summary',
      navGroup: NavGroup.EXPORT,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@export/components/pinterest-summary-export/PinterestSummaryExport'
          ),
      ),
    },
  }),

  'export.tiktok-summary': StateFactory({
    url: '/tiktok-summary-export',
    views: {
      main: {
        template: componentNameToTemplate('tikTokSummaryExport'),
      },
    },
    data: {
      title: 'TikTok Summary',
      navGroup: NavGroup.EXPORT,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@export/components/tiktok-summary-export/TikTokSummaryExport'
          ),
      ),
    },
  }),

  'export.instagram-summary': StateFactory({
    url: '/instagram-summary-export',
    views: {
      main: {
        template: componentNameToTemplate(
          'instagramAccountExportableContainer',
        ),
      },
    },
    data: {
      title: 'Instagram Summary',
      navGroup: NavGroup.EXPORT,
    },
  }),

  'export.instagram-schedule': StateFactory({
    url: `/instagram-schedule-export?:${DATE_RANGE_INTERVAL_PARAM}`,
    views: {
      main: {
        template: componentNameToTemplate('instagramScheduleExport'),
      },
    },
    data: {
      title: 'Instagram Schedule',
      navGroup: NavGroup.EXPORT,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@export/components/instagram-schedule-export/InstagramScheduleExport'
          ),
      ),
    },
  }),

  'export.instagram-feed-preview': StateFactory({
    url: '/instagram-feed-preview-export',
    views: {
      main: {
        template: componentNameToTemplate('instagramFeedPreviewExport'),
      },
    },
    data: {
      title: 'Instagram Feed Preview',
      navGroup: NavGroup.EXPORT,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@export/components/instagram-schedule-export/InstagramFeedPreviewExport'
          ),
      ),
    },
  }),

  'export.insights-summary': StateFactory({
    url: '/insights-summary-export',
    views: {
      main: {
        template: componentNameToTemplate('insightsSummaryExport'),
      },
    },
    data: {
      title: 'Insights Summary',
      navGroup: NavGroup.EXPORT,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@export/components/insights-summary-export/InsightsSummaryExport'
          ),
      ),
    },
  }),

  'export.insights-performance': StateFactory({
    url: '/client-performance-export',
    views: {
      main: {
        template: componentNameToTemplate('clientPerformanceExport'),
      },
    },
    data: {
      title: 'Client Performance',
      navGroup: NavGroup.EXPORT,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.InsightsClientPerformanceReport),
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@export/components/client-performance-export/ClientPerformanceExport'
          ),
      ),
    },
  }),
};
